import React, { InputHTMLAttributes, TextareaHTMLAttributes, forwardRef } from 'react';
import { forms, animations, cx } from '../../styles';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  helperText?: string;
  error?: string;
  fullWidth?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
  shape?: 'default' | 'pill';
  containerClassName?: string;
  labelClassName?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

/**
 * Input component with consistent styling
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({ 
  label,
  helperText,
  error,
  fullWidth = true,
  inputSize = 'md',
  shape = 'default',
  className = '',
  containerClassName = '',
  labelClassName = '',
  id,
  icon,
  iconPosition = 'right',
  ...props 
}, ref) => {
  const inputId = id || `input-${Math.random().toString(36).substring(2, 9)}`;
  
  const baseInputClasses = cx(
    forms.input,
    forms.inputSizes[inputSize],
    forms.inputShapes[shape],
    icon && iconPosition === 'left' ? 'pl-9' : '',
    icon && iconPosition === 'right' ? 'pr-9' : '',
    error ? 'border-red-500 focus:ring-red-500' : '', // Consider adding error state colors to the system
    fullWidth ? 'w-full' : '',
    animations.transition.default,
    className
  );

  return (
    <div className={cx('flex flex-col', containerClassName)}>
      {label && (
        <label 
          htmlFor={inputId} 
          className={cx(
            'mb-1 text-xs font-medium',
            error ? 'text-red-600' : 'text-tertiary',
            labelClassName
          )}
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        <input
          id={inputId}
          ref={ref}
          className={baseInputClasses}
          {...props}
        />
        
        {icon && (
          <div 
            className={cx(
              'absolute top-1/2 transform -translate-y-1/2',
              'text-tertiary',
              iconPosition === 'left' ? 'left-3' : 'right-3'
            )}
          >
            {icon}
          </div>
        )}
      </div>
      
      {(helperText || error) && (
        <p 
          className={cx(
            'mt-1 text-xs',
            error ? 'text-red-600' : 'text-tertiary'
          )}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
});

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: string;
  error?: string;
  fullWidth?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  rows?: number;
}

/**
 * TextArea component with consistent styling
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({
  label,
  helperText,
  error,
  fullWidth = true,
  className = '',
  containerClassName = '',
  labelClassName = '',
  id,
  rows = 3,
  ...props
}, ref) => {
  const textareaId = id || `textarea-${Math.random().toString(36).substring(2, 9)}`;
  
  const baseTextareaClasses = cx(
    forms.textarea,
    error ? 'border-red-500 focus:ring-red-500' : '', // Consider adding error state colors to the system
    fullWidth ? 'w-full' : '',
    animations.transition.default,
    className
  );

  return (
    <div className={cx('flex flex-col', containerClassName)}>
      {label && (
        <label 
          htmlFor={textareaId} 
          className={cx(
            'mb-1 text-xs font-medium',
            error ? 'text-red-600' : 'text-tertiary',
            labelClassName
          )}
        >
          {label}
        </label>
      )}
      
      <textarea
        id={textareaId}
        rows={rows}
        ref={ref}
        className={baseTextareaClasses}
        {...props}
      />
      
      {(helperText || error) && (
        <p 
          className={cx(
            'mt-1 text-xs',
            error ? 'text-red-600' : 'text-tertiary'
          )}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
TextArea.displayName = 'TextArea';
